/**
 * Deck dimensions — the single source of truth for where every part sits.
 *
 * Everything is in centimetres, in the deck's own frame: +x right,
 * +z toward the user, +y up. Free of Three.js and the DOM, so the solver
 * and the gesture maths can be unit-tested without a GPU.
 */

const footH = 1.3;
const bodyH = 5.2;
const platter = { x: -7.5, z: 0.5, r: 14.85, h: 2.7 } as const;
const recordR = 15.0;
const recordH = 0.18;

const top = footH + bodyH; /* 6.5 — the plinth surface */
const matTop = top + platter.h + 0.22;
const recordTop = matTop + recordH;

/**
 * The headshell, in the arm's own frame: where it hangs off the tube, how far
 * it is angled, and where the stylus tip hangs below it. `three/parts/tonearm`
 * builds the mesh from these, and the solver takes the stylus from them too,
 * so the needle the eye sees is the needle the maths puts on the groove.
 */
const head = { x: 21.1, z: -1.62, yaw: 0.36, drop: 0.32, tipX: 0.78, tipDrop: 1.97 } as const;

/**
 * How far the stylus tip hangs below the pivot when the needle is down. The
 * pivot is set from it: down, the tip meets the record surface exactly, and
 * the cue lift is what holds it clear.
 */
const stylusDrop = head.drop + head.tipDrop;

/** Tonearm pivot, its height above the plinth, and the stylus in the arm's frame. */
const arm = {
  x: 15.5,
  z: -11.0,
  y: recordTop - top + stylusDrop,
  /** Pivot to stylus tip, along the arm and across it — where the headshell puts it. */
  len: head.x + head.tipX * Math.cos(head.yaw),
  offZ: head.z - head.tipX * Math.sin(head.yaw),
  /** How far the counterweight reaches behind the pivot. */
  tail: 5.5,
  head,
} as const;

export const DIM = {
  deckW: 50,
  deckD: 42,
  footH,
  bodyH,
  platter,
  recordR,
  recordH,
  labelR: 5.03,
  arm,
  stylusDrop,

  /** Where the stylus sits at the start and end of a side. */
  leadInR: 14.3,
  runOutR: 5.55,

  top,
  platterY: top + platter.h / 2,
  matTop,
  recordY: matTop + recordH / 2,
  recordTop,
  armY: top + arm.y,
} as const;

/** Pitch fader: centre, travel length, and the percentage range it maps to. */
export const FADER = { x: 20.4, z: 8.0, len: 13.0, range: 8 } as const;

/** The plinth buttons: start/stop, the two speed pads and power. */
export const CONTROLS = {
  start: { x: -20.3, z: 16.2, r: 2.65 },
  speed: { z: 18.2, w: 4.4, d: 2.6, x33: -14.2, x45: -8.6 },
  power: { x: -21.6, z: -17.2, r: 1.4 },
} as const;

/**
 * The part of the deck a listener actually watches: the record, the whole
 * tonearm from counterweight to stylus, and the buttons in front of them.
 * The default view frames this, not the plinth — which is left to overflow.
 */
export const FOCUS = {
  minX: platter.x - recordR,
  maxX: arm.x + 2.7 /* the pivot's base */,
  minZ: arm.z - arm.tail,
  maxZ: CONTROLS.speed.z + CONTROLS.speed.d / 2,
  /** The plane it is framed on. */
  y: recordTop,
} as const;

export const RPM_33 = 33 + 1 / 3;
export const RPM_45 = 45;

/** A point on the deck's top plane. */
export interface PlanePoint {
  x: number;
  z: number;
}

export const PIVOT: PlanePoint = { x: DIM.arm.x, z: DIM.arm.z };
export const CENTRE: PlanePoint = { x: DIM.platter.x, z: DIM.platter.z };
